/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { Blockers, CountryText, HelpItem, SharingContent } from "./api.pb";
import type { BlockerAction } from "./blocker_action.pb";
import type { CardCustomizationData } from "./cards.pb";
import type { FormBlocker } from "./form_blocker.pb";
import type { RatePlan } from "./rate_plan.pb";

/**
 * ScenarioPlan describes the screens that the user should be shown to complete their scenario.
 * This includes blockers, supplemental data specific to the blockers, and additional data that can
 * result in screens such as cash waiting.
 */
export interface ScenarioPlan {
  /**
   * Blockers to complete the current scenario. Can be null if there are no blockers to resolve.
   * Deprecated in favor of BlockerDescriptor.
   *
   * @deprecated
   */
  blockers?: Blockers;
  /**
   * BlockersSupplement related to the blockers for the scenario.
   * Can be null if there are no blockers to resolve.
   * Deprecated in favor of BlockerDescriptor.
   *
   * @deprecated
   */
  blockers_supplement?: BlockersSupplement;
  /**
   * If true, onboarding should be immediately ended. The customer has already been onboarded on
   * this device. Deprecated in favor of blockers determining onboarding status.
   *
   * @deprecated
   */
  end_onboarding?: boolean;
  /**
   * Data to populate the pending payment screen. Null if the scenario isn't Onboarding or there
   * is no cash waiting.
   */
  cash_waiting_data?: CashWaitingData;
  /**
   * Data to populate the screens for inviting your friends. Null if the scenario isn't Onboarding
   * or if the customer shouldn't be asked to invite friends.
   */
  invite_friends_data?: InviteFriendsData;
  /**
   * Blockers to complete the current scenario. Can be null or empty if there are no blockers to
   * resolve.
   * Clients are expected to present blockers in the order they appear in `blocker_descriptors`.
   */
  blocker_descriptors?: BlockerDescriptor[];
  /**
   * When true, the app should display blockers in floating dialogs if possible. If the app doesn't
   * know how to display a blocker in a dialog, it will fallback to the default full screen
   * representation.
   */
  display_in_dialog?: boolean;
}

/** BlockerDescriptor describes both the semantics and the presentation of the blocker. */
export interface BlockerDescriptor {
  /**
   * Identify a particular BlockerDescriptor in a scenario. This enables the ability to drive
   * client behaviour from the server using an id to uniquely identify a particular blocker.
   * Eg. a selection blocker with an action that can go to a particular blocker id synchronously.
   */
  id?: string;
  /** Exactly one blocker will be populated. */
  blocker?: Blockers;
  /** Zero or one supplement will be populated. */
  supplement?: BlockersSupplement;
  /**
   * When true, the app can allow the user to skip this blocker. Or, the app can present this
   * blocker in combination with the next one.
   */
  skippable?: boolean;
  /** Customizable appearance values for this blocker. */
  theme?: BlockerDescriptorTheme;
  /**
   * The action to perform when the customer taps the "X" dismiss button blocker.
   *
   * If the `dismiss_action_display_behavior` is `CLIENT_CONTROLLED` (the default), then the
   * presence of this action does not imply that an "X" should be shown or hidden and only allows
   * for customization of the dismiss action such as showing a confirmation dialog with text should
   * an "X" already be included in the blocker based on client logic.
   *
   * If the `dismiss_action_display_behavior` is `SERVER_CONTROLLED`, then the presence of this
   * action does dictate whether or not the "X" button is shown for this blocker.
   */
  dismiss_action?: BlockerAction;
  /**
   * If set to `CLIENT_CONTROLLED` or unset, then the dismiss "X" will be displayed based on logic
   * baked into the client. If set to `SERVER_CONTROLLED` then the "X" will be displayed only if
   * `dismiss_action` is set.
   */
  dismiss_action_display_behavior?: BlockerDescriptorDismissActionDisplayBehavior;
  /**
   * When true in combination with skippable == true, the app will make a call to /2.0/cash/skip-blocker
   * to fetch the next scenario plan, forgoing any locally cached blockers in the current scenario plan.
   *
   * The motivation behind this is to aid in the migration off of franklin into plasma for flows, due
   * to the call and response paradigm in Plasma for each blocker. This paradigm allows the server to
   * make decisions and record completed/skipped states for each requirement.
   *
   * Keep in mind, enabling this will lead to another network round trip in a flow which may negatively
   * impact the customer experience with the tradeoff being that we would be able to skip computing the
   * blocker in the previous response.
   *
   * Client Version Availability (tenative):
   * iOS - 3.64
   * Android - 3.64
   *
   * Details at go/remote-skip-blocker-design.
   *
   * For other types of skip configurations, see go/blocker-ordering-and-skippability.
   */
  remote_skip?: boolean;
  /**
   * The client should collect all requests within a group and send them at once.
   * If the next blocker in the scenario plan's blocker array has a different group ID,
   * no group ID, or there are no more blockers, then the client should submit the responses
   * for all blockers in the group using a SubmitBlockerRequest.
   */
  group_id?: string;
}

/**
 * Defines whether the client or the server is the authority for displaying the dismiss action
 * (the "X" button) for the blocker.
 *
 * Traditionally client behavior was used and that remains the default. As new blocker cases arise
 * where the server wants to make exceptions to the client logic this behavior can be switched to
 * use the `SERVER_CONTROLLED` logic for that particular blocker.
 */
export enum BlockerDescriptorDismissActionDisplayBehavior {
  /**
   * CLIENT_CONTROLLED - The "X" for the blocker is displayed based on baked-in client logic.
   * This logic is roughly "show the X for all blockers that aren't in onboarding."
   */
  CLIENT_CONTROLLED = "CLIENT_CONTROLLED",
  /**
   * SERVER_CONTROLLED - The "X" for the blocker is displayed if `dismiss_action` is set to a valid
   * action. If `dismiss_action` is not set, then the "X" is not displayed.
   */
  SERVER_CONTROLLED = "SERVER_CONTROLLED",
}

/**
 * Describes the customizable aspects of a given screen. Most display attributes of a
 * screen are defined by the design system and are not customizable, but some attributes
 * depend on the specific content being displayed.
 *
 * The theme should only include attributes which are customizable within the design system,
 * and should not be used to do one-off customizations on a particular screen.
 *
 * Right now this is only the accent color but might include additional items in the future.
 */
export interface BlockerDescriptorTheme {
  /**
   * A color used to accent or "tint" the content of a given screen. This will be used for
   * things like the background color of the primary button, or to colorize single-color
   * template glyphs.
   *
   * This value is optional. If `null` then the standard "Cash Green" should be used.
   */
  accent_color?: Color;
}

/**
 * BlockersSupplement contains app-specific data about each blocker. For example, the screen to
 * set a user's name has different text based on the user's rate plan, thus when Blockers contains
 * a NameBlocker, BlockersSupplement will contain a NameBlockerSupplement with the user's rate plan.
 */
export interface BlockersSupplement {
  name?: NameBlockerSupplement;
  resolve_merge?: ResolveMergeBlockerSupplement;
  rate_plan?: RatePlanBlockerSupplement;
  card?: CardBlockerSupplement;
  confirm?: ConfirmBlockerSupplement;
  email?: EmailBlockerSupplement;
  address?: AddressBlockerSupplement;
  passcode_verification?: PasscodeVerificationBlockerSupplement;
  instrument_verification?: InstrumentVerificationBlockerSupplement;
  email_verification?: EmailVerificationBlockerSupplement;
  phone_verification?: PhoneVerificationBlockerSupplement;
  passcode_creation?: PasscodeCreationBlockerSupplement;
  identity_verification?: IdentityVerificationBlockerSupplement;
  passcode_and_expiration?: CardPasscodeAndExpirationBlockerSupplement;
  selection?: SelectionBlockerSupplement;
  signature?: SignatureBlockerSupplement;
  file?: FileBlockerSupplement;
  region?: RegionBlockerSupplement;
  phone_number?: PhoneNumberBlockerSupplement;
  contact_verification?: ContactVerificationBlockerSupplement;
  cash_waiting?: CashWaitingBlockerSupplement;
  invite_friends?: InviteFriendsBlockerSupplement;
  scheduled_transaction?: ScheduledTransactionBlockerSupplement;
  order_confirmation?: OrderConfirmationBlockerSupplement;
  government_id?: GovernmentIdBlockerSupplement;
  card_design?: CardDesignBlockerSupplement;
}

/** Supplemental data for resolving a NameBlocker in the app. */
export interface NameBlockerSupplement {
  /** The customer's current rate plan, used to describe the name as a full name vs a display name. */
  rate_plan?: RatePlan;
  /**
   * The main text to display on the name entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  name_prefill?: string;
  /**
   * If name_prefill hasn't been provided and this is non-null, this should be used as light grey
   * hint text for the entry field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /**
   * Minimum and maximum number of characters allowed in the name. If null, the scenario doesn't
   * have specific requirements around name length.
   */
  min_length?: number;
  max_length?: number;
  /**
   * The text to display at the footer of the name entry screen. If non-null, the footer text is
   * to be shown just above the button(s). This text may include markdown-style links that the
   * client must parse and linkify.
   * Example: "By continuing you agree to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
}

/** Supplemental data for resolving a ResolveMergeBlocker in the app. */
export interface ResolveMergeBlockerSupplement {
  /** Message giving the customer context for confirming the given merge. */
  confirm_message?: string;
  /**
   * If present, this message should be displayed as an additional confirmation before
   * skipping the merge.
   */
  skip_message?: string;
}

/** Supplemental data for resolving a RatePlanBlocker in the app. */
export interface RatePlanBlockerSupplement {
  /** The data needed to show screens when the customer can select either rate plan. */
  rate_plan_config?: RatePlanConfig;
  /** The data needed to show screens when the customer is forced to select the business rate plan. */
  forced_rate_plan_config?: ForcedRatePlanConfig;
}

/** Server-driven configuration data relating to selecting a rate plan. */
export interface RatePlanConfig {
  /** e.g. "Personal" */
  personal_title?: string;
  /** e.g. "Send money between friends and family" */
  personal_description?: string;
  /** e.g. "Business" */
  business_title?: string;
  /** e.g. "Get paid for goods and services" */
  business_description?: string;
  /** The transaction fee for business use, in basis points (e.g. 150 = 1.5%) */
  business_fee_bps?: number;
  /** e.g. "No limits, free to send and just 1.5% to receive" */
  business_text1?: string;
  /** e.g. "Pick a Cashtag, your unique handle for getting paid" */
  business_text2?: string;
  /** e.g. "Share a link to your online profile to get paid" */
  business_text3?: string;
  /** e.g. "$max" */
  sample_cashtag?: string;
  /** A list of IntendedUsageItems to display on the rate plan selection screen. */
  intended_usage_items?: IntendedUsageItem[];
}

/**
 * IntendedUsageItem encapsulates data about an intended usage to display on the rate plan
 * selection screen.
 */
export interface IntendedUsageItem {
  /** An opaque string to the client. In practice it's the name of an IntendedUsage. */
  intended_usage?: string;
  rate_plan?: RatePlan;
  display_text?: string;
  /**
   * Config for presenting a screen to confirm the rate plan. If present, the app
   * should show the confirmation screen. Will always be present for business rate
   * plans.
   */
  confirm_rate_plan_config?: ConfirmRatePlanConfig;
}

/** Configuration for the rate plan confirmation screen. */
export interface ConfirmRatePlanConfig {
  /** The title of the screen, such as "Cash for Business". */
  title?: string;
  /** A list of 3 sections to display, each with an icon and text. */
  confirm_rate_plan_sections?: ConfirmRatePlanSection[];
  /**
   * Text to display in a dialog when a customer attempts to go back to the rate plan selection
   * screen. e.g. "Are you sure you want to go back? A Cash for Business account is required to
   * get paid for services or goods."
   */
  retreat_confirmation_text?: string;
}

/** Represents a section in the rate plan confirmation screen, with an icon and text. */
export interface ConfirmRatePlanSection {
  /** e.g. "No limits, free to send and just 2.75% to receive". */
  text?: string;
  /** The icon to show for this item. */
  icon?: ConfirmRatePlanSectionIcon;
  /** Some icons have text rendered inside them. e.g. "$max" or "2.75%". */
  icon_text?: string;
}

export enum ConfirmRatePlanSectionIcon {
  /** BUSINESS_ICON - The standard icon that denotes a business. Looks like two buildings. */
  BUSINESS_ICON = "BUSINESS_ICON",
  /** CIRCLE - A circle. Typically with text in it like "2.75%" or "$". */
  CIRCLE = "CIRCLE",
}

/** Server-driven configuration data relating to selecting a forced rate plan. */
export interface ForcedRatePlanConfig {
  /**
   * e.g. "A Cash Pro account is required to deposit payments for goods and services. It's free to
   * send and just 1.5% to receive."
   */
  main_text?: string;
  /** e.g. "Upgrade" */
  button_text?: string;
  /** e.g. "Support" */
  inline_button_title?: string;
  /** e.g. "We're here to help if you have questions" */
  inline_button_text?: string;
  /** e.g. "https://squareup.com/help/contact?prefill=cash_pro_upgrade" */
  inline_button_url?: string;
  /** e.g. "Cash for Business" */
  title?: string;
}

/** Supplemental data for resolving a RegionBlocker in the app. */
export interface RegionBlockerSupplement {
  /** A list of countries to display for selection. */
  display_countries?: DisplayCountry[];
}

export interface DisplayCountry {
  country_code?: Country;
  /**
   * The url of the region flag icon.
   *
   * @deprecated
   */
  icon_url?: string;
  /** Country's canonical name. */
  country_name?: string;
  /** Whether or not the country is currently supported. */
  supported?: boolean;
}

/** Supplemental data for resolving a CardBlocker in the app. */
export interface CardBlockerSupplement {
  /** The menu item for selecting to enter a bank account by routing and account number. */
  menu_item_bank_account?: MenuItem;
  /**
   * The menu item for skipping instrument linking. This will only be populated if the
   * ClientScenario is ONBOARDING.
   */
  menu_item_skip?: MenuItem;
  /**
   * The main text to display on the card or bank account linking screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display. Added for v2.12.
   */
  main_text?: string;
  /** The hint text to show when the card number field is empty. */
  card_number_hint_text?: string;
  /** @deprecated */
  bank_account_oauth_config?: OAuthConfig;
  /** Contain bank account linking preferences, will replace bank_account_oauth_config and yodlee_enabled eventually. */
  bank_account_linking_config?: BankAccountLinkingConfig;
  linked_card_input_variant?: CardBlockerSupplementLinkedCardInputVariant;
  /** Whether or not to upload the Bouncer payload scanned during OCR. */
  should_upload_ocr_payload?: boolean;
  /** Treatment name for use in experiment outcome measurements. * */
  treatment?: string;
  /** Whether or not to upload the card image scanned during OCR. */
  should_upload_ocr_scan_image?: boolean;
}

/**
 * Controls the input flow for customers trying to provide credit/debit card info for linking.
 * Reference: https://www.notion.so/cashappcash/Bouncer-OCR-Integration-b9ab75b64aa3450c97fa3aa65ae2d3ff
 */
export enum CardBlockerSupplementLinkedCardInputVariant {
  LEGACY = "LEGACY",
  MANUAL_ENTRY_ONLY = "MANUAL_ENTRY_ONLY",
  MANUAL_ENTRY_WITH_SCAN_OPTION = "MANUAL_ENTRY_WITH_SCAN_OPTION",
  SCAN_FIRST = "SCAN_FIRST",
  SCAN_FIRST_MULTI_SCREEN = "SCAN_FIRST_MULTI_SCREEN",
}

export interface OAuthConfig {
  /** URL to launch in webview. */
  launch_url?: string;
  /** When redirected to url which conforms to the regex, close the webview, capture the url and send to the server. */
  success_url_regex?: string;
  /** When redirected to a url which conforms to the regex, close the webview, and proceed as though the flow canceled. */
  cancel_url_regex?: string;
  /** When redirected to a url which conforms to the regex, close the webview, and proceed with the next step in the flow. */
  skip_url_regex?: string;
  /**
   * When redirected to a url which conforms to the regex, this client knows the page is ready to be displayed.
   * The initial Plaid Link load event happen before the page is actually ready to be shown, causing a spinner to be shown.
   */
  start_url_regex?: string;
  /** Indicate when a customer has searched for an institution in the Plaid webview. */
  search_url_regex?: string;
  /** The name of the OAuth flow. This value is returned in the `oauth-resolve-flow` request. */
  flow_type?: OAuthConfigFlowType;
  /** JavaScript to execute on page load. */
  onload?: string;
  /** This is used since 3.27 to represent the Plaid privacy pane. 3.27 clients show the Plaid privacy pane using the FormBlocker set here. */
  privacy_blocker?: FormBlocker;
}

export enum OAuthConfigFlowType {
  /** PLAID_AUTH - The oauth-resolve-flow will execute the Plaid auth flow. */
  PLAID_AUTH = "PLAID_AUTH",
  /** PLAID_RELINK - The oauth-resolve-flow will execute the Plaid relink flow. */
  PLAID_RELINK = "PLAID_RELINK",
}

export interface BankAccountLinkingConfig {
  /**
   * Configuration for linking a bank account via a third party OAuth flow. When this is present the client
   * should not call /link-bank-account. It should open a webview to the provided launch-url.
   * Then close the webview and return the URL contents when the webview redirects to the URL
   * matching the redir_regexp.
   *
   * Only valid when supported_instrument_types include the BANK_ACCOUNT.
   */
  bank_account_oauth_config?: OAuthConfig;
  /** Whether linking accounts via Yodlee is enabled. */
  yodlee_enabled?: boolean;
  /**
   * Whether the option for a user to manually enter their ACH details is enabled.
   * (on the Plaid WebView)
   * This should be false during, for example, the Enhanced Verification flow,
   * as it is not supported.
   *
   * [Note]
   * There's a situation where a customer should start with a screen to manually
   * link a bank account without using an OAuth service. As mentioned above,
   * the flag is not intended for this purpose. To achieve it, force_manual_ach
   * is used.
   *
   * [TODO]
   * This flag might be moved to OAuthConfig as it's used by an OAuth service only.
   */
  manual_ach_enabled?: boolean;
  /**
   * Force a user to manually enter their ACH details instead of asking them to go
   * through a third-party service such as Plaid to link a bank account.
   */
  force_manual_ach?: boolean;
  plaid_linking_config?: PlaidLinkingConfig;
  supported_linking_flows?: BankAccountLinkingConfigSupportedLinkingFlow[];
  /**
   * Multiple 3rd party flows may be supported, but one flow can be preferred over another
   * based off of some business logic
   */
  preferred_linking_flow?: BankAccountLinkingConfigSupportedLinkingFlow;
}

/**
 * For a given Bank, these are the supported linking mechanisms. Not all third party flows
 * support all banks
 */
export enum BankAccountLinkingConfigSupportedLinkingFlow {
  /** MANUAL - Users manually enter in banking information. */
  MANUAL = "MANUAL",
  /** PLAID - We route the linking to Plaid. */
  PLAID = "PLAID",
  /** STRIPE - We route the linking to Stripe. */
  STRIPE = "STRIPE",
}

export interface PlaidLinkingConfig {
  /** Whether to use Plaid Update Mode which is used to refresh a user's access_token. */
  use_update_mode?: boolean;
  /** Additional Plaid products to request access to.  The 'auth' product is added by default. */
  additional_plaid_products?: PlaidLinkingConfigPlaidProduct[];
}

export enum PlaidLinkingConfigPlaidProduct {
  UNSUPPORTED = "UNSUPPORTED",
  /** ASSETS - An existing link can be used when adding this product type. */
  ASSETS = "ASSETS",
}

/** A menu item in the app. */
export interface MenuItem {
  header_text?: string;
  description_text?: string;
}

/**
 * Supplemental data for resolving a ConfirmBlocker in the app. If menu items are supplied here,
 * they should be used; otherwise the app should use the button(s) at the bottom of the screen.
 *
 * Note that the deprecated MenuItem fields are ignored as of client version 3.3
 */
export interface ConfirmBlockerSupplement {
  /** The title for the confirmation page. */
  main_text?: string;
  /**
   * The header and description for the button to confirm sending this payment.
   *
   * @deprecated
   */
  menu_item_confirm_payment?: MenuItem;
  /**
   * The header and description for the button to pay the suspected duplicate payment and abandon
   * the current payment. Will be null if there is no suspected duplicate payment, or that payment
   * is already complete and cannot be confirmed.
   *
   * @deprecated
   */
  menu_item_pay_duplicate?: MenuItem;
  /** The title for the button to confirm sending this payment. */
  confirm_payment_button_title?: string;
  /**
   * The title for the button to pay the suspected duplicate payment and abandon
   * the current payment. Will be null if there is no suspected duplicate payment, or that payment
   * is already complete and cannot be confirmed.
   */
  pay_duplicate_button_title?: string;
}

/** Supplemental data for resolving an EmailBlocker in the app. */
export interface EmailBlockerSupplement {
  /** The text to show on the email linking screen. */
  main_text?: string;
  /** The text to show above the main text. */
  headline?: string;
}

/** Supplemental data for resolving a PhoneNumberBlocker in the app. */
export interface PhoneNumberBlockerSupplement {
  /** The text to show on the SMS linking screen. */
  main_text?: string;
  input_hint_text?: string;
}

/** Supplemental data for resolving an AddressBlocker in the app. */
export interface AddressBlockerSupplement {
  /**
   * The main text to display on the address entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /**
   * The customer's current address. When not null, should be used to pre-populate the
   * entry fields.
   */
  address_prefill?: GlobalAddress;
  /**
   * The hint text to show when the address field is empty. Only used when typeahead is enabled.
   * Example: "Mailing Address"
   */
  hint_text?: string;
  /**
   * The text to display at the footer of the address entry screen. If non-null, the footer text is
   * to be shown just above the button(s). This text may include markdown-style links that the
   * client must parse and linkify.
   * Example: "By continuing you agree to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
  /**
   * The smaller text to display on the address entry screen under the main_text. When non-null,
   * this text should be shown instead of empty space.
   */
  sub_text?: string;
}

/** Supplemental data for resolving a PasscodeVerificationBlocker in the app. */
export interface PasscodeVerificationBlockerSupplement {
  /**
   * The main text to display on the passcode entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /** If true, the forgot passcode button should be suppressed. */
  suppress_forgot_passcode_button?: boolean;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
}

/** Supplemental data for resolving a PasscodeCreationBlocker in the app. */
export interface PasscodeCreationBlockerSupplement {
  /**
   * The text to display on the passcode creation screen. When non-null, this text should
   * be shown instead of the text that the client would otherwise display.
   */
  prompt_main_text?: string;
  /**
   * The text to display on the passcode verification screen. When non-null, this text should
   * be shown instead of the text that the client would otherwise display.
   */
  confirmation_main_text?: string;
  /** If true, the blocker can be skipped. */
  skippable?: boolean;
  /**
   * The smaller text to display on the passcode creation screen under the main_text. When non-null,
   * this text should be shown instead of empty space.
   */
  prompt_sub_text?: string;
  /**
   * The smaller text to display on the passcode verification screen under the main_text. When non-null,
   * this text should be shown instead of empty space.
   */
  confirmation_sub_text?: string;
}

export interface InstrumentVerificationBlockerSupplement {
  main_text?: string;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
  input_hint_text?: string;
}

export interface ContactVerificationBlockerSupplement {
  /**
   * The main text to display on the code entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
}

export interface EmailVerificationBlockerSupplement {
  /**
   * If present, this message should be displayed as a confirmation before
   * skipping the verification.
   */
  skip_message?: string;
  /**
   * The main text to display on the code entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /** Text to show above the main text. */
  headline?: string;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
  suppress_edit_email_address?: boolean;
}

export interface PhoneVerificationBlockerSupplement {
  /**
   * If present, this message should be displayed as a confirmation before
   * skipping the verification.
   */
  skip_message?: string;
  /**
   * The main text to display on the code entry screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
  suppress_edit_phone_number?: boolean;
  /** The status of the phone verification */
  status?: PhoneVerificationBlockerSupplementPhoneVerificationBlockerStatus;
}

export enum PhoneVerificationBlockerSupplementPhoneVerificationBlockerStatus {
  PVBS_DO_NOT_USE = "PVBS_DO_NOT_USE",
  SUCCESS = "SUCCESS",
  INVALID = "INVALID",
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  DUPLICATE_SMS_NUMBER = "DUPLICATE_SMS_NUMBER",
  CUSTOMER_DOES_NOT_EXIST = "CUSTOMER_DOES_NOT_EXIST",
  INVALID_VERIFICATION_CODE = "INVALID_VERIFICATION_CODE",
  EXPIRED_VERIFICATION_CODE = "EXPIRED_VERIFICATION_CODE",
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}

/** Supplemental data for resolving an IdentityVerificationBlocker in the app. */
export interface IdentityVerificationBlockerSupplement {
  /**
   * The main text to display on the name screen. When non-null, this text should be shown instead
   * of the text that the client would otherwise display. Note that there is currently an assumption
   * that the name screen will be the first in the IDV flow.
   */
  name_main_text?: string;
  /** If non-null, this should be used to pre-populate the entry field on the name screen. */
  name_prefill?: string;
  /**
   * If name_prefill hasn't been provided and this is non-null, this should be used as light grey
   * hint text for the entry field on the name screen, which disappears once the user starts typing.
   */
  name_hint_text?: string;
  /**
   * The text to show when prompting for SSN (full or last four). When non-null, this text should be
   * shown instead of the text that the client would otherwise display.
   */
  ssn_main_text?: string;
  /**
   * The text to show when prompting for birthdate. When non-null, this text should be
   * shown instead of the text that the client would otherwise display.
   */
  birthdate_main_text?: string;
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
  /**
   * The latest birthdate, specified in ISO 8601 format, for which the client should consider the
   * user to be an adult and therefore prompt for additional verification information. If the user
   * enters a birthdate after (and not equal to) this date, then the client should not prompt for
   * SSN or address even if the other fields indicate that they are required.
   *
   * If the value is null then the entered birthdate does not affect whether or not the SSN screen
   * is shown.
   */
  child_threshold_birthdate?: string;
}

export interface SelectionBlockerSupplement {
  /** Help items to display in addition to the default set for this blocker. */
  additional_help_items?: HelpItem[];
  /** If specified, use this color for the main text. */
  main_text_color?: string;
  /** If specified, use this color for the footer text. */
  footer_text_color?: string;
}

export interface SignatureBlockerSupplement {
  /**
   * The main text to display on the card signature screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /**
   * The text to display on the card signature screen after a stamp has been added. When non-null,
   * this text should be shown instead of the text that the client would otherwise display.
   */
  stamp_added_text?: string;
  /**
   * The text to display on the card signature screen when customer returns to draw mode after
   * adding stamps. When non-null, this text should be shown instead of the text that the client
   * would otherwise display.
   */
  return_to_draw_mode_text?: string;
  /**
   * The text to display below the card confirmation. When non-null, this text should be shown
   * instead of the text the client would otherwise display.
   */
  confirm_screen_text?: string;
  /**
   * The minimum and maximum amount of ink the client should accept as a valid signature. The
   * value will fall in the range of [0..1].
   */
  min_ink_coverage?: number;
  max_ink_coverage?: number;
  /** Error messages to use if the signature does not meet the ink coverage requirements. */
  not_enough_ink_message?: string;
  too_much_ink_message?: string;
}

export interface CardDesignBlockerSupplement {
  /**
   * The main text to display on the card signature screen. When non-null, this text
   * should be shown instead of the text that the client would otherwise display.
   */
  main_text?: string;
  /**
   * The text to display on the card signature screen after a stamp has been added. When non-null,
   * this text should be shown instead of the text that the client would otherwise display.
   */
  stamp_added_text?: string;
  /**
   * The text to display on the card signature screen when customer returns to draw mode after
   * adding stamps. When non-null, this text should be shown instead of the text that the client
   * would otherwise display.
   */
  return_to_draw_mode_text?: string;
  /**
   * The minimum and maximum amount of ink the client should accept as a valid signature. The
   * value will fall in the range of [0..1].
   */
  min_ink_coverage?: number;
  max_ink_coverage?: number;
  /** Error messages to use if the signature does not meet the ink coverage requirements. */
  not_enough_ink_message?: string;
  too_much_ink_message?: string;
  /** Text to display over customizable area (e.g., "Tap to customize"). */
  customization_prompt_text?: string;
  /** Text to display on cashtag toggle (e.g., "Show Cashtag"). */
  cashtag_toggle_text?: string;
}

/** Supplemental data for resolving a CardPasscodeAndExpirationBlocker in the app. */
export interface CardPasscodeAndExpirationBlockerSupplement {
  /** The main text to display on the passcode entry screen. */
  passcode_main_text?: string;
  /** The main text to display on the expiration date entry screen. */
  expiration_main_text?: string;
  /** The localized version of the main text to display on the passcode entry screen. */
  localized_passcode_main_text?: LocalizableString;
  /** The localized version of the main text to display on the expiration date entry screen. */
  localized_expiration_main_text?: LocalizableString;
}

/** Supplemental data for resolving a FileBlocker in the app. */
export interface FileBlockerSupplement {
  /** The main text to display on the screen. */
  main_text?: string;
  help_items?: HelpItem[];
  confirmation_main_text?: string;
}

export interface CashWaitingBlockerSupplement {
  cash_waiting_data?: CashWaitingData;
}

export interface InviteFriendsBlockerSupplement {
  invite_friends_data?: InviteFriendsData;
}

/**
 * Server-driven data for populating the cash waiting screen in app onboarding. The cash waiting
 * can be the result of a payment or a referral.
 */
export interface CashWaitingData {
  /** The url of the photo of the payment sender. Can be null if the sender has no photo. */
  photo_url?: string;
  /** The text that describes the sender of the payment or invitation. e.g. "From Max Rhodes" */
  sender_text?: string;
  /** The amount to show in large text. e.g. "$35" */
  amount?: Money;
  /**
   * The main text of the screen. e.g. "You can deposit cash directly into your bank account
   * using your debit card."
   */
  main_text?: string;
  /** The text of the button. e.g. "Deposit" */
  button_text?: string;
  /**
   * True if the cash waiting is for a payment that is subject to fees based on rate plan
   * and should be displayed after picking a rate plan.
   */
  is_subject_to_fees?: boolean;
}

/** Server-driven data for populating the invite friends screens in app onboarding. */
export interface InviteFriendsData {
  /** The configuration for inviting friends including what treatment to use and the bounty amount. */
  invitation_config?: InvitationConfig;
  /**
   * The text that describes the invitation program. e.g. "Invite your friends and we'll send you
   * both $5 when they try Cash!"
   */
  promo_text?: string;
}

/** Server-driven configuration data relating to referral invitations. */
export interface InvitationConfig {
  /**
   * The SMS message text that should be used when sending the invitations. Deprecated in favor of
   * preview_message and message_template in 2.6.
   *
   * @deprecated
   */
  message?: string;
  /** The amount that the customer will get as a bounty for each invited contact that joins Cash. */
  bounty_amount?: Money;
  /**
   * True if the app should allow the customer to invite contacts. If false, the app should skip
   * inviting contacts.
   */
  enabled?: boolean;
  invitation_treatment?: InvitationConfigInvitationTreatment;
  /**
   * The amount that the referee will get as a welcome bonus after joining Cash.
   *
   * @deprecated
   */
  welcome_bonus_amount?: Money;
  /** The header text to be shown to the user. */
  header_text?: string;
  /** The message that the client uses to pre-fill the message preview window. */
  preview_message?: string;
  preview_message_by_country?: CountryText[];
  /**
   * The full message to be sent for SMS invitations. The client must replace {{message}} with the
   * message that the customer has provided, or the preview_message provided by the server.
   */
  message_template?: string;
  /** True if the app should allow the customer to invite all contacts. */
  invite_all_enabled?: boolean;
  /**
   * Variants in an experiment to measure the effectiveness of different invitation behaviors.
   * Deprecated September 2018: always set to V2_6_NO_INTERSTITIAL (or null).
   *
   * @deprecated
   */
  invitation_behavior?: InvitationConfigInvitationBehavior;
  /** The customer's reward code, if available. */
  reward_code?: string;
  /** The URL for the customer's reward code page, if available. */
  reward_code_url?: string;
  /** The content used when an invitation is shared. */
  sharing_content?: SharingContent;
  /** Text to display under the header text, e.g. a reward URL, to link to sharing flow. May be null. */
  header_link_display_text?: string;
  /** Text to display on the button leading to the invitation screen from the profile screen. */
  profile_button_text?: string;
  /** Text to display on the button leading to the invitation screen from the activity screen. */
  activity_button_text?: string;
  /**
   * Templated text to display on the button leading to the invitation screen from the profile
   * screen. The client must replace {{name}} with the contact's name.
   */
  profile_button_template?: string;
}

/** Variants in an A/B/C experiment to improve the SMS invitation flow. */
export enum InvitationConfigInvitationTreatment {
  /** BATCH_TEN - The original treatment. Batch 10 contacts into a single SMS message. */
  BATCH_TEN = "BATCH_TEN",
  /** BATCH_ONE - Similar to the original treatment, except that a new SMS message is created for each contact. */
  BATCH_ONE = "BATCH_ONE",
  /**
   * COMPOSE_IMMEDIATELY - Tapping a contact immediately opens the screen to compose an SMS to them. There is no
   * "Select all" in this variant.
   */
  COMPOSE_IMMEDIATELY = "COMPOSE_IMMEDIATELY",
  /**
   * ADDITIONAL_ALIAS - This invitation represents an additional alias of a contact that was invited by a different
   * alias. Treating this as an InvitationTreatment let's us determine the effectiveness of
   * these additional invitations.
   */
  ADDITIONAL_ALIAS = "ADDITIONAL_ALIAS",
}

export enum InvitationConfigInvitationBehavior {
  V2_5_BEHAVIOR = "V2_5_BEHAVIOR",
  V2_6_NO_EMAIL = "V2_6_NO_EMAIL",
  V2_6_NO_INTERSTITIAL = "V2_6_NO_INTERSTITIAL",
  V2_6_NO_EMAIL_NO_INTERSTITIAL_SELECT_ALL = "V2_6_NO_EMAIL_NO_INTERSTITIAL_SELECT_ALL",
}

/** Supplemental data for the ScheduledTransactionBlocker. */
export interface ScheduledTransactionBlockerSupplement {
  /** The funding source to pull from, eg: "Visa Debit XXXX" or "Bank Account XXX". */
  funding_source?: string;
}

export interface OrderConfirmationBlockerSupplement {
  /** Card preview to confirm. */
  card_preview?: CardCustomizationData;
  /** Line items corresponding to the order. */
  line_items?: OrderConfirmationBlockerSupplementLineItem[];
  /**
   * The text to display at the footer of the confirmation screen. If non-null, the footer text is
   * to be shown just above the button(s). This text may include markdown-style links that the
   * client must parse and linkify.
   * Example: "By continuing you agree to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
  /** Label for the button to confirm the order */
  submit_button_text?: string;
}

export interface OrderConfirmationBlockerSupplementLineItem {
  /** The main title of the item in the order. */
  name?: string;
  /** A description of the line item. */
  detail_text?: string;
  /** The price of the item. This is a string so $0 items can be presented as "Free". */
  price?: string;
}

export interface GovernmentIdBlockerSupplement {
  main_text?: string;
  confirmation_main_text?: string;
}
